import { useState } from 'react'
import type { ReviewStats } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

const W = 720
const H = 220
const PAD = { l: 40, r: 16, t: 14, b: 30 }
const Y_MIN = 4.5

/**
 * Monthly average rating with review volume behind it, this year's months
 * against the same months last year (two lines), so seasonality reads
 * directly instead of through a rolling window.
 */
export function MonthlyChart({ stats }: { stats: ReviewStats }) {
  const [hover, setHover] = useState<number | null>(null)
  const months = stats.monthly
  if (months.length < 2) return null
  // Split the 24-month series into last year / this year by calendar year.
  const thisYear = stats.year_over_year.as_of.slice(0, 4)
  const lastYear = String(Number(thisYear) - 1)
  const byMonth = (year: string) =>
    Array.from({ length: 12 }, (_, i) => {
      const key = `${year}-${String(i + 1).padStart(2, '0')}`
      return months.find(m => m.month === key) ?? { month: key, reviews: 0, avg: null, sub_five: 0 }
    })
  const ty = byMonth(thisYear)
  const ly = byMonth(lastYear)
  const maxVol = Math.max(1, ...ty.map(m => m.reviews), ...ly.map(m => m.reviews))

  const x = (i: number) => PAD.l + ((i + 0.5) / 12) * (W - PAD.l - PAD.r)
  const y = (v: number) => PAD.t + (1 - (v - Y_MIN) / (5 - Y_MIN)) * (H - PAD.t - PAD.b)
  const yVol = (n: number) => H - PAD.b - (n / maxVol) * (H - PAD.t - PAD.b) * 0.6
  const line = (series: typeof ty) =>
    series
      .map((m, i) =>
        m.avg == null ? null : `${x(i).toFixed(1)},${y(Math.max(Y_MIN, m.avg)).toFixed(1)}`
      )
      .reduce<string[]>((acc, pt, i, arr) => {
        if (pt == null) return acc
        const prev = i > 0 ? arr[i - 1] : null
        acc.push(`${prev == null ? 'M' : 'L'}${pt}`)
        return acc
      }, [])
      .join(' ')
  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const px = ((e.clientX - rect.left) / rect.width) * W
    const i = Math.floor(((px - PAD.l) / (W - PAD.l - PAD.r)) * 12)
    setHover(Math.min(11, Math.max(0, i)))
  }
  const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rating by month, this year against last</CardTitle>
        <CardDescription>
          Monthly average rating (lines) with the number of reviews that month behind it (bars). The
          axis starts at {Y_MIN.toFixed(1)}: below that is a bad month, not a bad axis.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative w-full overflow-x-auto">
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="h-auto w-full min-w-[480px]"
            role="img"
            aria-label={`Monthly average rating ${thisYear} vs ${lastYear}`}
            onMouseMove={onMove}
            onMouseLeave={() => setHover(null)}
          >
            {[4.5, 4.75, 5].map(v => (
              <g key={v}>
                <line
                  x1={PAD.l}
                  x2={W - PAD.r}
                  y1={y(v)}
                  y2={y(v)}
                  stroke="currentColor"
                  className="text-white/10"
                />
                <text
                  x={PAD.l - 6}
                  y={y(v) + 3}
                  textAnchor="end"
                  className="fill-textMuted text-[10px]"
                >
                  {v.toFixed(2)}
                </text>
              </g>
            ))}
            {ty.map((m, i) => (
              <g key={i}>
                <rect
                  x={x(i) - 14}
                  y={yVol(ly[i].reviews)}
                  width={12}
                  height={H - PAD.b - yVol(ly[i].reviews)}
                  fill="rgba(255,255,255,0.07)"
                />
                <rect
                  x={x(i) + 2}
                  y={yVol(m.reviews)}
                  width={12}
                  height={H - PAD.b - yVol(m.reviews)}
                  fill="rgba(255,255,255,0.16)"
                />
                <text
                  x={x(i)}
                  y={H - 10}
                  textAnchor="middle"
                  className="fill-textMuted text-[10px]"
                >
                  {MONTH[i]}
                </text>
              </g>
            ))}
            <path
              d={line(ly)}
              fill="none"
              stroke="rgba(255,255,255,0.35)"
              strokeWidth={2}
              strokeDasharray="4 3"
            />
            <path d={line(ty)} fill="none" stroke="#00ba82" strokeWidth={2.5} />
            {ty.map((m, i) =>
              m.avg == null ? null : (
                <circle key={i} cx={x(i)} cy={y(Math.max(Y_MIN, m.avg))} r={3.5} fill="#00ba82" />
              )
            )}
            {hover != null && (
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={PAD.t}
                y2={H - PAD.b}
                stroke="currentColor"
                className="text-white/30"
                strokeDasharray="3 3"
              />
            )}
          </svg>
          {hover != null && (
            <div
              className="pointer-events-none absolute top-2 z-10 rounded-md border border-borderStrong bg-surface2 px-3 py-1.5 text-xs text-text shadow-md"
              style={{
                left: `${(100 * (x(hover) > W * 0.7 ? x(hover) - 190 : x(hover) + 10)) / W}%`,
              }}
            >
              <div className="font-semibold">{MONTH[hover]}</div>
              <div>
                <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-mint" />
                {thisYear}: {ty[hover].avg?.toFixed(2) ?? 'no reviews'} · {ty[hover].reviews}{' '}
                reviews
                {ty[hover].sub_five ? `, ${ty[hover].sub_five} below five` : ''}
              </div>
              <div>
                <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-white/40" />
                {lastYear}: {ly[hover].avg?.toFixed(2) ?? 'no reviews'} · {ly[hover].reviews}{' '}
                reviews
                {ly[hover].sub_five ? `, ${ly[hover].sub_five} below five` : ''}
              </div>
            </div>
          )}
        </div>
        <div className="mt-2 flex gap-4 text-[11px] text-textMuted">
          <span>
            <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-mint" />
            {thisYear}
          </span>
          <span>
            <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-white/40" />
            {lastYear}
          </span>
          <span>
            <i className="mr-1 inline-block h-2 w-2 rounded-sm bg-white/15" />
            reviews that month
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
